import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../contexts/auth-context";
import { useHistory, useLocation } from "react-router-dom";
import LineChartCard from "./Helpers/Charts/LineChartCard";
import FederationSummaryCard from "./Helpers/Charts/FederationSummaryCard";
import DataTable, { DataTableColumn, DataTableRow } from "./Helpers/DataTable";
import { useBreadcrumb } from "../Breadcrumb/BreadcrumbContext";
import { useSidebar } from "../NewSideBar/SidebarContext";
import DatePickerModal from "../DatePicker";
import DrillDownDrawer, {
  DrillDownTarget,
  DrillEndpoint,
  MonthOption,
} from "./Helpers/DrillDownDrawer";
import LayoutContainer from "../LayoutContainer";
import styles from "./managementDashboard.module.css";
import KPICardContainer from "./Helpers/KPICard/KPICardContainer";
import { KPICardData } from "./constant";
import { useDashboardContext } from "../../contexts/dashboard-context/DashboardContext";
import SimpleBackIcon from "../../assets/simpleBackIcon.svg";
import config from "../../config";
import PageNotFound from "../PageNotFound";
import { formatAdserverType } from "../../utils";
import { useToast } from "../../contexts/toast-context";
import RelativeTime from "../RelativeTime";

export const formatCount = (n: number): string => {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
};

const fmtLocalDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const defaultDateRange = () => {
  const now = new Date();
  return {
    start: fmtLocalDate(
      new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
    ),
    end: fmtLocalDate(now),
  };
};

const ManagementDashboard: React.FC = () => {
  const history = useHistory();
  const location = useLocation<
    { applicationIds?: string[]; selectedFedIds?: string[] } | undefined
  >();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [dateRange, setDateRange] = useState(defaultDateRange);

  const {
    dashboardData,
    isLoading,
    isFulfilled,
    lastRefreshedAt,
    refetch,
    dataErrors,
  } = useDashboardContext();
  const { setBreadcrumbItems } = useBreadcrumb();
  const { mainMenuItems, setActiveSubItem } = useSidebar();

  const {
    tableGetOrganizationActivationEnrichmentMonthLevelCount,
    tableGetOrganizationActivationEnrichmentDayLevelCount,
    appMetadata,
    getAllApplications,
    getFederationsByApplicationIds,
    getAdserversByOrgId,
  } = useContext(AuthContext);
  const { showToast } = useToast();

  const authRole =
    (appMetadata && (Object.values(appMetadata) as any[])[0]?.roles?.[0]) || "";
  const isPlatformOrAppAdmin =
    authRole === config.superUserRole ||
    authRole === config.applicationAdminRole;

  const [fedEventType, setFedEventType] = useState<string | null>(null);
  const [orgEventType, setOrgEventType] = useState<string | null>(null);
  const [adserverEventType, setAdserverEventType] = useState<string | null>(
    null,
  );

  // Top-level application filter
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);
  const [allApps, setAllApps] = useState<{ id: string; name: string }[]>([]);
  const [appDropdownOpen, setAppDropdownOpen] = useState(false);
  const appDropdownRef = useRef<HTMLDivElement>(null);

  // Top-level federation filter
  const [selectedFedIds, setSelectedFedIds] = useState<string[]>([]);
  const [fedFilterDropdownOpen, setFedFilterDropdownOpen] = useState(false);
  const fedFilterDropdownRef = useRef<HTMLDivElement>(null);

  // Federations, organizations, and adserver types fetched for the selected
  // Proton apps. Kept as one state object so all three lists stay in sync.
  const [appLinkedOptions, setAppLinkedOptions] = useState<{
    federations: { id: string; name: string }[];
    organizations: { id: string; name: string }[];
    adserverTypes: string[] | null;
  }>({ federations: [], organizations: [], adserverTypes: null });
  const {
    federations: appFederationOptions,
    organizations: appOrganizationOptions,
    adserverTypes: appAdserverTypes,
  } = appLinkedOptions;
  const [fedsByAppLoading, setFedsByAppLoading] = useState(false);
  const fedFetchGenRef = useRef(0);

  // Searchable dropdown query state
  const [appSearchQuery, setAppSearchQuery] = useState("");
  const [fedSearchQuery, setFedSearchQuery] = useState("");

  // Drill-down drawer
  const [drillTarget, setDrillTarget] = useState<DrillDownTarget | null>(null);

  // Activation enrichment table month filter + day-level data
  const [selectedTableMonth, setSelectedTableMonth] = useState<string | null>(
    null,
  );
  const [tableDayRows, setTableDayRows] = useState<DataTableRow[]>([]);
  const [tableDayLoading, setTableDayLoading] = useState(false);

  // Dedicated monthly table data (from table-specific API)
  const [tableMonthData, setTableMonthData] = useState<any[]>([]);
  const [tableMonthLoading, setTableMonthLoading] = useState(false);
  const [tableMonthError, setTableMonthError] = useState(false);

  const [chartResetKey, setChartResetKey] = useState(0);
  const [tableTab, setTableTab] = useState<"activation" | "enrichment">(
    "activation",
  );

  // Helper: build the IDs that every API call should send.
  // Rule 1 – app IDs:  selected ones, or ALL apps when nothing is chosen.
  // Rule 2 – fed IDs:  selected ones; when an app is selected but no fed is,
  //                    send ALL feds for those apps (from appFederationOptions);
  //                    when nothing at all is selected, omit fed IDs and let
  //                    the backend return everything.
  const buildApiFilters = (opts?: {
    overrideAppIds?: string[];
    overrideFedIds?: string[];
    overrideAppFeds?: { id: string; name: string }[];
    overrideOrgIds?: string[];
  }) => {
    const appIds = opts?.overrideAppIds ?? selectedAppIds;
    const fedIds = opts?.overrideFedIds ?? selectedFedIds;
    const appFeds = opts?.overrideAppFeds ?? appFederationOptions;

    const effectiveAppIds =
      appIds.length > 0 ? appIds : allApps.map((a) => a.id);
    const effectiveFedIds =
      fedIds.length > 0
        ? fedIds
        : appIds.length > 0
          ? appFeds.map((f) => f.id)
          : [];
    const effectiveOrgIds =
      opts?.overrideOrgIds !== undefined
        ? opts.overrideOrgIds
        : appIds.length > 0
          ? appOrganizationOptions.map((o) => o.id)
          : [];
    return { effectiveAppIds, effectiveFedIds, effectiveOrgIds };
  };

  const clearAllFilters = () => {
    fedFetchGenRef.current += 1;
    setFedEventType(null);
    setOrgEventType(null);
    setAdserverEventType(null);
    setSelectedTableMonth(null);
    setSelectedAppIds([]);
    setSelectedFedIds([]);
    setAppLinkedOptions({ federations: [], organizations: [], adserverTypes: null });
    setFedsByAppLoading(false);
    setAppSearchQuery("");
    setFedSearchQuery("");
    setChartResetKey((k) => k + 1);
    const defaultRange = defaultDateRange();
    setDateRange(defaultRange);
    const { effectiveAppIds } = buildApiFilters({
      overrideAppIds: [],
      overrideFedIds: [],
      overrideAppFeds: [],
      overrideOrgIds: [],
    });
    refetch(defaultRange.start, defaultRange.end, effectiveAppIds, [], []);
  };

  const fetchFederationsForApps = (appIds: string[]) => {
    if (!appIds.length) {
      fedFetchGenRef.current += 1;
      setAppLinkedOptions({ federations: [], organizations: [], adserverTypes: null });
      setFedsByAppLoading(false);
      return;
    }
    const gen = ++fedFetchGenRef.current;
    setFedsByAppLoading(true);
    setAppLinkedOptions({ federations: [], organizations: [], adserverTypes: null });
    getFederationsByApplicationIds({ args: { application_ids: appIds } })
      .then((res: any) => {
        if (gen !== fedFetchGenRef.current) return;
        const federations = (res?.federations ?? []).map((f: any) => ({
          id: f.id,
          name: f.name,
        }));
        const organizations = (res?.organizations ?? []).map((o: any) => ({
          id: o.id,
          name: o.name,
        }));
        setAppLinkedOptions({ federations, organizations, adserverTypes: null });

        // Fetch adserver types for all linked orgs in parallel (non-blocking).
        // Each org's registry entry lists ad_server (pure ad-server type) and
        // cloud (cloud storage type) — both map to destination_adserver_type in
        // dashboard data, so we collect both fields.
        if (!organizations.length) {
          // App has no linked orgs → no adservers possible; set to empty array
          // so the filter actively excludes everything rather than staying null
          // (null = "not yet loaded, skip filtering").
          setAppLinkedOptions((prev) => ({ ...prev, adserverTypes: [] }));
        } else {
          Promise.allSettled(
            organizations.map((o: { id: string }) =>
              getAdserversByOrgId({ args: { organizationId: o.id } }),
            ),
          )
            .then((adserverResults) => {
              if (gen !== fedFetchGenRef.current) return;
              const typeSet = new Set<string>();
              for (const result of adserverResults) {
                if (result.status === "fulfilled") {
                  for (const item of (result.value as any)?.ad_server ?? []) {
                    if (item.ad_server) typeSet.add(item.ad_server);
                    if (item.cloud) typeSet.add(item.cloud);
                  }
                }
              }
              setAppLinkedOptions((prev) => ({
                ...prev,
                adserverTypes: Array.from(typeSet),
              }));
            })
            .catch(() => {
              // Adserver type filtering is best-effort; fail silently.
            });
        }
      })
      .catch((err: any) => {
        if (gen !== fedFetchGenRef.current) return;
        setAppLinkedOptions({ federations: [], organizations: [], adserverTypes: null });
        showToast(
          err?.message ||
            "Failed to load federations for the selected application(s).",
          "error",
        );
      })
      .finally(() => {
        if (gen !== fedFetchGenRef.current) return;
        setFedsByAppLoading(false);
      });
  };

  const resetToAllProtons = () => {
    fedFetchGenRef.current += 1;
    setSelectedAppIds([]);
    setSelectedFedIds([]);
    setAppLinkedOptions({ federations: [], organizations: [], adserverTypes: null });
    setFedsByAppLoading(false);
    setSelectedTableMonth(null);
    const { effectiveAppIds } = buildApiFilters({
      overrideAppIds: [],
      overrideFedIds: [],
      overrideAppFeds: [],
      overrideOrgIds: [],
    });
    refetch(dateRange.start, dateRange.end, effectiveAppIds, [], []);
  };

  const handleAppToggle = (id: string | null) => {
    if (id === null) {
      resetToAllProtons();
      return;
    }
    const next = selectedAppIds.includes(id)
      ? selectedAppIds.filter((x) => x !== id)
      : [...selectedAppIds, id];
    if (!next.length) {
      resetToAllProtons();
      return;
    }
    setSelectedAppIds(next);
    setSelectedFedIds([]);
    setSelectedTableMonth(null);
    // Scope via filter_federation_ids only — wait for feds, then refetch in the effect below.
    // An unscoped refetch here returns global totals and races the scoped call.
    fetchFederationsForApps(next);
  };

  const handleFedFilterToggle = (id: string | null) => {
    const { effectiveAppIds, effectiveOrgIds } = buildApiFilters();
    if (id === null) {
      setSelectedFedIds([]);
      setSelectedTableMonth(null);
      // No specific fed selected → send all feds for selected apps
      const allAppFedIds = appFederationOptions.map((f) => f.id);
      refetch(
        dateRange.start,
        dateRange.end,
        effectiveAppIds,
        allAppFedIds,
        effectiveOrgIds,
      );
      return;
    }
    const next = selectedFedIds.includes(id)
      ? selectedFedIds.filter((x) => x !== id)
      : [...selectedFedIds, id];
    setSelectedFedIds(next);
    setSelectedTableMonth(null);
    refetch(dateRange.start, dateRange.end, effectiveAppIds, next, effectiveOrgIds);
  };

  // Build start/end ISO dates for a given month index + year.
  // Uses local date parts (not toISOString) to avoid UTC timezone shift.
  const buildMonthRange = (
    year: number,
    monthIdx: number,
  ): { startDate: string; endDate: string } => {
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    return {
      startDate: fmt(new Date(year, monthIdx, 1)),
      endDate: fmt(new Date(year, monthIdx + 1, 0)),
    };
  };

  // Derive the list of months covered by the current date range
  const availableMonths = (): MonthOption[] => {
    if (!dateRange.start || !dateRange.end) return [];
    const months: MonthOption[] = [];
    // Parse as local time (append T00:00:00) to avoid UTC date shift
    const start = new Date(dateRange.start + "T00:00:00");
    const end = new Date(dateRange.end + "T00:00:00");
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cur <= end) {
      const { startDate, endDate } = buildMonthRange(
        cur.getFullYear(),
        cur.getMonth(),
      );
      months.push({
        label: cur.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        }),
        startDate,
        endDate,
      });
      cur.setMonth(cur.getMonth() + 1);
    }
    return months;
  };

  useEffect(() => {
    setBreadcrumbItems([
      { label: "Discover", path: "/entry-hub?console=Discover" },
      { label: "Curation", path: "/federation-selection" },
      { label: "Management Dashboard", path: "/management-dashboard" },
    ]);
    const sideBarMenuToHighlight = mainMenuItems
      .flatMap((item) => item.subMenu)
      .find((subItem) =>
        subItem.urls.some((url) => url.includes("/management-dashboard")),
      )?.label;
    setActiveSubItem(sideBarMenuToHighlight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore app/federation filter state when navigating back from a details page.
  // The details page Back button injects { applicationIds, selectedFedIds } into
  // location.state so the user's selection is preserved across page transitions.
  useEffect(() => {
    const state = location.state;
    if (!state?.applicationIds?.length) return;
    const appIds = state.applicationIds;
    const fedIds = state.selectedFedIds ?? [];
    setSelectedAppIds(appIds);
    setSelectedFedIds(fedIds);
    fetchFederationsForApps(appIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch all applications for the top-level filter dropdown
  useEffect(() => {
    getAllApplications({ args: {} })
      .then((res: any) => {
        setAllApps(
          (res?.items ?? [])
            .filter((a: any) => a.name?.trim())
            .map((a: any) => ({ id: a.id, name: a.name })),
        );
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch dashboard after app-linked federations resolve.
  // The guard for selectedFedIds is intentionally removed so that state
  // restoration (which pre-sets selectedFedIds before appFederationOptions
  // loads) also triggers the correct scoped refetch.
  useEffect(() => {
    if (!selectedAppIds.length || !appFederationOptions.length) return;
    const { effectiveAppIds, effectiveFedIds, effectiveOrgIds } = buildApiFilters();
    refetch(
      dateRange.start,
      dateRange.end,
      effectiveAppIds,
      effectiveFedIds,
      effectiveOrgIds,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appFederationOptions]);

  // Close application dropdown on outside click and reset search
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        appDropdownRef.current &&
        !appDropdownRef.current.contains(e.target as Node)
      ) {
        setAppDropdownOpen(false);
        setAppSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close federation dropdown on outside click and reset search
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        fedFilterDropdownRef.current &&
        !fedFilterDropdownRef.current.contains(e.target as Node)
      ) {
        setFedFilterDropdownOpen(false);
        setFedSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch monthly table data whenever date range or top-level filters change
  useEffect(() => {
    if (!dateRange.start || !dateRange.end) return;
    // allApps loads asynchronously; wait for it so this doesn't fire once
    // with no app/fed filters and immediately get aborted when allApps arrives.
    if (!allApps.length) return;
    // Wait for Proton federations — table APIs scope via filter_federation_ids only.
    if (selectedAppIds.length > 0 && !appFederationOptions.length) {
      setTableMonthLoading(fedsByAppLoading);
      setTableMonthData([]);
      setTableMonthError(false);
      return;
    }
    const controller = new AbortController();
    setTableMonthLoading(true);
    setTableMonthError(false);
    setTableMonthData([]);
    const { effectiveAppIds, effectiveFedIds } = buildApiFilters();
    tableGetOrganizationActivationEnrichmentMonthLevelCount({
      args: {
        start_date: dateRange.start,
        end_date: dateRange.end,
        ...(effectiveAppIds.length ? { application_ids: effectiveAppIds } : {}),
        ...(effectiveFedIds.length ? { federation_ids: effectiveFedIds } : {}),
      },
      signal: controller.signal,
    })
      .then((res: any) => {
        if (!controller.signal.aborted) {
          const data: any[] = res?.data ?? [];
          setTableMonthData(data);
          setTableMonthLoading(false);
          // Auto-select the tab that has data if the current tab would be empty
          setTableTab((prev) => {
            const hasActivation = data.some(
              (r) =>
                String(r.activation_enrichment).toLowerCase() === "activation",
            );
            const hasEnrichment = data.some(
              (r) =>
                String(r.activation_enrichment).toLowerCase() === "enrichment",
            );
            if (prev === "activation" && !hasActivation && hasEnrichment)
              return "enrichment";
            if (prev === "enrichment" && !hasEnrichment && hasActivation)
              return "activation";
            return prev;
          });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setTableMonthError(true);
          setTableMonthLoading(false);
        }
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRange.start,
    dateRange.end,
    selectedAppIds,
    selectedFedIds,
    appFederationOptions,
    allApps,
    fedsByAppLoading,
  ]);

  // Fetch day-level org data when a specific month is selected in the table
  useEffect(() => {
    if (!selectedTableMonth) return;
    const parsed = new Date(`${selectedTableMonth} 1 00:00:00`);
    if (isNaN(parsed.getTime())) return;
    const { startDate, endDate } = buildMonthRange(
      parsed.getFullYear(),
      parsed.getMonth(),
    );
    const controller = new AbortController();
    setTableDayLoading(true);
    setTableDayRows([]);
    const { effectiveAppIds, effectiveFedIds } = buildApiFilters();
    tableGetOrganizationActivationEnrichmentDayLevelCount({
      args: {
        start_date: startDate,
        end_date: endDate,
        ...(effectiveAppIds.length ? { application_ids: effectiveAppIds } : {}),
        ...(effectiveFedIds.length ? { federation_ids: effectiveFedIds } : {}),
      },
      signal: controller.signal,
    })
      .then((res: any) => {
        if (!controller.signal.aborted) {
          const data: any[] = res?.data ?? [];
          const grouped: Record<string, any> = {};
          for (const r of data) {
            const key = `${r.organization_id}::${r.event_type}::${r.activation_enrichment}`;
            if (!grouped[key]) {
              grouped[key] = {
                organization_name: r.organization_name || r.organization_id,
                event_type: r.event_type ?? "",
                activation_enrichment: r.activation_enrichment ?? "",
                total_count: 0,
                query_count: 0,
              };
            }
            grouped[key].total_count += r.total_count ?? 0;
            grouped[key].query_count += r.query_count ?? 0;
          }
          setTableDayRows(
            Object.values(grouped).map((r, i) => ({
              id: String(i),
              organization_name: r.organization_name,
              event_type: r.event_type,
              activation_enrichment: r.activation_enrichment,
              total_count: r.total_count,
              query_count: r.query_count,
            })),
          );
          setTableDayLoading(false);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setTableDayLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTableMonth]);

  const openDrill = (
    cardTitle: string,
    eventType?: string | null,
    monthLabelStr?: string,
    endpoint?: DrillEndpoint,
    entityParams?: Record<string, string>,
    chartOptions?: {
      dayLevelChartMetric?: "total_count" | "query_count";
      allowedDimensionKeys?: string[];
    },
  ) => {
    const months = availableMonths();
    if (monthLabelStr) {
      const [mon, yr] = monthLabelStr.split(" ");
      const monthIdx = new Date(`${mon} 1 ${yr} 00:00:00`).getMonth();
      const { startDate } = buildMonthRange(parseInt(yr, 10), monthIdx);
      setDrillTarget({
        cardTitle,
        availableMonths: months,
        defaultMonthStartDate: startDate,
        eventType: eventType ?? undefined,
        endpoint,
        entityParams,
        ...chartOptions,
      });
    } else {
      setDrillTarget({
        cardTitle,
        availableMonths: months,
        eventType: eventType ?? undefined,
        endpoint,
        entityParams,
        ...chartOptions,
      });
    }
  };

  const handleDateApply = (start: string, end: string) => {
    const s = start.split("T")[0];
    const e = end.split("T")[0];
    setDateRange({ start: s, end: e });
    const { effectiveAppIds, effectiveFedIds, effectiveOrgIds } = buildApiFilters();
    refetch(s, e, effectiveAppIds, effectiveFedIds, effectiveOrgIds);
  };

  const formatDateLabel = () => {
    if (!dateRange.start || !dateRange.end) return "Select date range";
    const fmt = (d: string) => {
      const [y, m, day] = d.split("-");
      return `${day}/${m}/${y}`;
    };
    return `${fmt(dateRange.start)} - ${fmt(dateRange.end)}`;
  };

  const activeFedCount = (() => {
    if (!dashboardData) return 0;
    if (dashboardData.uniqueActiveFederationCount !== null)
      return dashboardData.uniqueActiveFederationCount;
    return new Set(
      dashboardData.federationMonth
        .filter((r) => r.total_count > 0 || r.query_count > 0)
        .map((r) => r.federation_id),
    ).size;
  })();

  // When Proton(s) are selected, use the dedicated endpoint result which is
  // accurate and fast.  Fall back to federationMonth-derived list when no
  // Proton filter is active (shows all feds that had activity in the period).
  const federationOptions: { id: string; name: string }[] =
    selectedAppIds.length > 0
      ? appFederationOptions
      : dashboardData
        ? Array.from(
            new Map(
              dashboardData.federationMonth.map((r) => [
                r.federation_id,
                r.federation_name || r.federation_id,
              ]),
            ).entries(),
          ).map(([id, name]) => ({ id, name }))
        : [];

  // Hide previous Proton metrics while feds load; empty when Proton has no feds.
  const dashboardLoading = isLoading || fedsByAppLoading;
  const viewData =
    selectedAppIds.length > 0 &&
    !fedsByAppLoading &&
    appFederationOptions.length === 0
      ? null
      : dashboardData;

  const kpiCards = viewData
    ? [
        {
          ...KPICardData[0],
          value: activeFedCount,
          rawValue: activeFedCount,
        },
        {
          ...KPICardData[1],
          value: formatCount(
            viewData.eventType.find((e) => e.event_type === "query")
              ?.query_count ?? 0,
          ),
          rawValue:
            viewData.eventType.find((e) => e.event_type === "query")
              ?.query_count ?? 0,
        },
        {
          ...KPICardData[2],
          value: formatCount(
            viewData.eventType.find((e) => e.event_type === "marked_engaged")
              ?.query_count ?? 0,
          ),
          rawValue:
            viewData.eventType.find((e) => e.event_type === "marked_engaged")
              ?.query_count ?? 0,
        },
        {
          ...KPICardData[3],
          value: formatCount(
            viewData.eventType.find((e) => e.event_type === "query")
              ?.total_count ?? 0,
          ),
          rawValue:
            viewData.eventType.find((e) => e.event_type === "query")
              ?.total_count ?? 0,
        },
        {
          ...KPICardData[4],
          value: formatCount(
            viewData.eventType.find((e) => e.event_type === "marked_engaged")
              ?.total_count ?? 0,
          ),
          rawValue:
            viewData.eventType.find((e) => e.event_type === "marked_engaged")
              ?.total_count ?? 0,
        },
        (() => {
          const activationTotal = viewData.orgActivationEnrichmentMonth
            .filter((r) => r.activation_enrichment === "activation")
            .reduce((sum, r) => sum + r.total_count, 0);
          const enrichmentTotal = viewData.orgActivationEnrichmentMonth
            .filter((r) => r.activation_enrichment === "enrichment")
            .reduce((sum, r) => sum + r.total_count, 0);
          return {
            ...KPICardData[5],
            value: formatCount(
              viewData.eventType.find(
                (e) => e.event_type === "audience_transferred",
              )?.total_count ?? 0,
            ),
            rawValue:
              viewData.eventType.find(
                (e) => e.event_type === "audience_transferred",
              )?.total_count ?? 0,
            subRow: [
              {
                label: "Activation",
                value: formatCount(activationTotal),
                rawValue: activationTotal,
              },
              {
                label: "Enrichment",
                value: formatCount(enrichmentTotal),
                rawValue: enrichmentTotal,
              },
            ],
          };
        })(),
      ]
    : KPICardData;

  // Colour palette shared across the three summary cards
  const ENTITY_COLORS = [
    "#2563EB",
    "#F59E0B",
    "#10B981",
    "#EF4444",
    "#8B5CF6",
    "#06B6D4",
    "#F97316",
    "#84CC16",
    "#EC4899",
    "#14B8A6",
  ];

  // Helper: pivot entity-level month data into per-entity lines.
  // Each data point: { month_label, {entityKey}_total, {entityKey}_query }
  // Lines: one per active entity (non-zero data).
  function pivotByEntity<
    T extends {
      total_count: number;
      query_count: number;
      month_name: string;
      year: number;
      [key: string]: any;
    },
  >(
    rows: T[],
    getKey: (r: T) => string,
    getLabel: (key: string) => string,
    getMonthLabel: (r: T) => string,
  ) {
    const activeKeys = Array.from(
      new Set(
        rows.filter((r) => r.total_count > 0 || r.query_count > 0).map(getKey),
      ),
    );
    const monthMap: Record<string, Record<string, any>> = {};
    for (const r of rows) {
      const month = getMonthLabel(r);
      if (!monthMap[month]) monthMap[month] = { month_label: month };
      const k = getKey(r);
      monthMap[month][`${k}_total`] =
        (monthMap[month][`${k}_total`] ?? 0) + r.total_count;
      monthMap[month][`${k}_query`] =
        (monthMap[month][`${k}_query`] ?? 0) + r.query_count;
    }
    // Fill every active key to 0 for months where it has no data,
    // so Recharts shows all entries in the tooltip (not just defined ones).
    for (const monthData of Object.values(monthMap)) {
      for (const k of activeKeys) {
        if (monthData[`${k}_total`] === undefined) monthData[`${k}_total`] = 0;
        if (monthData[`${k}_query`] === undefined) monthData[`${k}_query`] = 0;
      }
    }

    // Detect duplicate display names — disambiguate by appending short id suffix
    const rawLabels = activeKeys.map((k) => getLabel(k));
    const labelCount: Record<string, number> = {};
    for (const l of rawLabels) labelCount[l] = (labelCount[l] ?? 0) + 1;
    const labelSeen: Record<string, number> = {};
    const disambiguated = activeKeys.map((k) => {
      const label = getLabel(k);
      if (labelCount[label] <= 1) return label;
      labelSeen[label] = (labelSeen[label] ?? 0) + 1;
      // append short suffix from key to disambiguate (e.g. "Group M ·2")
      const suffix = k.split("::")[0]?.slice(0, 4) ?? String(labelSeen[label]);
      return `${label} ·${suffix}`;
    });

    return {
      data: Object.values(monthMap),
      lines: activeKeys.map((k, i) => ({
        key: k,
        name: disambiguated[i],
        color: ENTITY_COLORS[i % ENTITY_COLORS.length],
      })),
    };
  }

  const monthLabel = (r: { month_name: string; year: number }) =>
    `${r.month_name} ${r.year}`;

  // Build label lookup: composite key → display name.
  // Key = federation_id::federation_name so that:
  //   - same id / different names → separate lines (distinct distributor relationships)
  //   - same name / different ids → separate lines (different feds that share a name)
  const fedLabelMap: Record<string, string> = {};
  const orgLabelMap: Record<string, string> = {};
  if (dashboardData) {
    for (const r of dashboardData.federationMonth) {
      const k = `${r.federation_id}::${r.federation_name}`;
      fedLabelMap[k] = r.federation_name || r.federation_id.slice(0, 8);
    }
    for (const r of dashboardData.organisationMonth) {
      const k = `${r.organization_id}::${r.organization_name}`;
      orgLabelMap[k] = r.organization_name || r.organization_id.slice(0, 8);
    }
  }

  // Unique event types per dataset (for sidebar dropdowns)
  const fedEventTypes = dashboardData
    ? Array.from(
        new Set(
          dashboardData.federationMonth
            .map((r) => r.event_type)
            .filter(Boolean),
        ),
      )
    : [];
  const orgEventTypes = dashboardData
    ? Array.from(
        new Set(
          dashboardData.organisationMonth
            .map((r) => r.event_type)
            .filter(Boolean),
        ),
      )
    : [];
  const adserverEventTypes = dashboardData
    ? Array.from(
        new Set(
          dashboardData.adserverMonth.map((r) => r.event_type).filter(Boolean),
        ),
      )
    : [];

  // Federation wise: composite key, display name as label.
  // Narrow to the federations selected in the top-level filter (when any are
  // selected) so this card always matches that selection regardless of what
  // the backend response happens to include.
  const { data: federationChartData, lines: federationLines } = viewData
    ? pivotByEntity(
        viewData.federationMonth
          .filter((r) => !fedEventType || r.event_type === fedEventType)
          .filter(
            (r) =>
              selectedFedIds.length === 0 ||
              selectedFedIds.includes(r.federation_id),
          ),
        (r) => `${r.federation_id}::${r.federation_name}`,
        (k) => fedLabelMap[k] ?? k.split("::")[1] ?? k.slice(0, 8),
        monthLabel,
      )
    : { data: [], lines: [] };

  // Organization wise: composite key, display name as label.
  // When a Proton is selected, narrow to orgs returned by getFederationsByApplicationIds
  // so the card reflects only the organizations that belong to that application.
  // Gated on selectedAppIds (not appOrganizationOptions.length) so an app that
  // genuinely has zero linked orgs renders an empty chart instead of falling
  // back to every organization.
  const appOrgIdSet = new Set(appOrganizationOptions.map((o) => o.id));
  const { data: orgChartData, lines: orgLines } = viewData
    ? pivotByEntity(
        (orgEventType
          ? viewData.organisationMonth.filter(
              (r) => r.event_type === orgEventType,
            )
          : viewData.organisationMonth
        ).filter(
          (r) =>
            selectedAppIds.length === 0 || appOrgIdSet.has(r.organization_id),
        ),
        (r) => `${r.organization_id}::${r.organization_name}`,
        (k) => orgLabelMap[k] ?? k.split("::")[1] ?? k.slice(0, 8),
        monthLabel,
      )
    : { data: [], lines: [] };

  // Full org option list for the entity multiselect dropdown — includes
  // orgs with zero activity in the current period (shown in grey in the
  // dropdown but without a chart line).  Active orgs keep their assigned
  // chart colour; inactive orgs use a neutral grey.
  const orgLineColorMap = new Map(orgLines.map((l) => [l.key.split("::")[0], l.color]));
  const orgActiveKeySet = new Set(orgLines.map((l) => l.key.split("::")[0]));
  const extraOrgLines =
    selectedAppIds.length > 0
      ? appOrganizationOptions
          .filter((o) => !orgActiveKeySet.has(o.id))
          .map((o) => ({
            key: o.id,
            name: o.name || o.id.slice(0, 8),
            color: "#CBD5E1",
          }))
      : [];
  // Remap active orgLines to use consistent registry colour (in case of no
  // match we fall back to the pivotByEntity-assigned colour).
  const allOrgLines = [
    ...orgLines.map((l) => ({
      ...l,
      color: orgLineColorMap.get(l.key.split("::")[0]) ?? l.color,
    })),
    ...extraOrgLines,
  ];

  // Adserver wise — client-side filter to app-linked adserver types when an
  // app is selected, mirroring the org filter above.  appAdserverTypes is
  // built from the registry (getAdserversByOrgId) for each app-linked org
  // so it is accurate even when filter_federation_ids doesn't scope adserver
  // data on the backend.
  const appAdserverTypeSet = appAdserverTypes !== null ? new Set(appAdserverTypes) : null;
  const { data: adserverChartData, lines: adserverLines } = viewData
    ? pivotByEntity(
        (adserverEventType
          ? viewData.adserverMonth.filter(
              (r) => r.event_type === adserverEventType,
            )
          : viewData.adserverMonth
        ).filter(
          (r) =>
            selectedAppIds.length === 0 ||
            appAdserverTypeSet === null ||
            appAdserverTypeSet.has(r.destination_adserver_type),
        ),
        (r) => r.destination_adserver_type,
        (type) => formatAdserverType(type),
        monthLabel,
      )
    : { data: [], lines: [] };

  // Full adserver option list for the entity multiselect — includes types
  // registered in the app's orgs that have zero activity in the current
  // period.  Active types keep their chart colour; inactive ones use grey.
  const adserverLineColorMap = new Map(adserverLines.map((l) => [l.key, l.color]));
  const adserverActiveTypeSet = new Set(adserverLines.map((l) => l.key));
  const extraAdserverLines =
    selectedAppIds.length > 0 && appAdserverTypes !== null
      ? appAdserverTypes
          .filter((type) => !adserverActiveTypeSet.has(type))
          .map((type) => ({
            key: type,
            name: formatAdserverType(type),
            color: "#CBD5E1",
          }))
      : [];
  const allAdserverLines = [
    ...adserverLines.map((l) => ({
      ...l,
      color: adserverLineColorMap.get(l.key) ?? l.color,
    })),
    ...extraAdserverLines,
  ];

  // Pivot eventMonth rows → one object per month with both total_count and query_count per event type
  // Keys: {event_type}_total and {event_type}_count — LineChartCard toggles between them
  const lineChartData = viewData
    ? (() => {
        const map: Record<string, Record<string, any>> = {};
        for (const r of viewData.eventMonth) {
          const label = `${r.month_name} ${r.year}`;
          if (!map[label]) map[label] = { month_label: label };
          map[label][`${r.event_type}_total`] = r.total_count;
          map[label][`${r.event_type}_count`] = r.query_count;
        }
        return Object.values(map);
      })()
    : [];

  // Unique months in order of appearance for the month pill filter
  const tableMonthOptions = Array.from(
    new Set(tableMonthData.map((r) => `${r.month_name} ${r.year}`)),
  );

  // Group by organization_id + event_type, summing counts across months
  const allTableRows: DataTableRow[] = (() => {
    const grouped: Record<
      string,
      {
        organization_name: string;
        event_type: string;
        activation_enrichment: string;
        total_count: number;
        query_count: number;
      }
    > = {};
    for (const r of tableMonthData) {
      const key = `${r.organization_id}::${r.event_type}::${r.activation_enrichment}`;
      if (!grouped[key]) {
        grouped[key] = {
          organization_name: r.organization_name || r.organization_id,
          event_type: r.event_type,
          activation_enrichment: r.activation_enrichment,
          total_count: 0,
          query_count: 0,
        };
      }
      grouped[key].total_count += r.total_count;
      grouped[key].query_count += r.query_count;
    }
    return Object.values(grouped).map((r, i) => ({
      id: String(i),
      organization_name: r.organization_name,
      event_type: r.event_type,
      activation_enrichment: r.activation_enrichment,
      total_count: r.total_count,
      query_count: r.query_count,
    }));
  })();

  // Filter monthly rows by active tab
  const filteredTableRows = allTableRows.filter(
    (r) => String(r.activation_enrichment).toLowerCase() === tableTab,
  );

  // Filter day-level rows by active tab (API returns both workflow types)
  const filteredDayRows = tableDayRows.filter(
    (r) => String(r.activation_enrichment).toLowerCase() === tableTab,
  );

  // Switch between monthly and day-level view
  const tableRows = selectedTableMonth ? filteredDayRows : filteredTableRows;
  const tableLoading = selectedTableMonth ? tableDayLoading : tableMonthLoading;

  const formatEventType = (val: string | number) =>
    String(val)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const formatCountVolume = (val: string | number) => {
    return Number(val).toLocaleString("en-US");
  };

  const tableColumns: DataTableColumn[] = selectedTableMonth
    ? [
        { label: "Organization", key: "organization_name" },
        {
          label: "Workflow Type",
          key: "activation_enrichment",
          formatter: formatEventType,
        },
        {
          label: "Audience Volume",
          key: "total_count",
          formatter: formatCountVolume,
        },
        { label: "Transfers Completed", key: "query_count" },
      ]
    : [
        { label: "Organization", key: "organization_name" },
        { label: "Event Type", key: "event_type", formatter: formatEventType },
        {
          label: "Workflow Type",
          key: "activation_enrichment",
          formatter: formatEventType,
        },
        {
          label: "Audience Volume",
          key: "total_count",
          formatter: formatCountVolume,
        },
        { label: "Transfers Completed", key: "query_count" },
      ];

  if (!isPlatformOrAppAdmin) return <PageNotFound />;

  return (
    <LayoutContainer>
      <div className={styles.actionsBar}>
        <div className={styles.actionsBarLeft}>
          <button className={styles.backBtn} onClick={() => history.goBack()}>
            <img src={SimpleBackIcon} alt="back" />
            <span>Back</span>
          </button>
        </div>

        <div className={styles.actionsBarRight}>
          {/* Proton filter */}
          <div className={styles.prominentFilterGroup}>
            <span className={styles.prominentFilterLabel}>Protons</span>
            <div className={styles.filterDropdownWrapper} ref={appDropdownRef}>
              <button
                className={`${styles.prominentFilterBtn} ${selectedAppIds.length > 0 ? styles.prominentFilterBtnActive : ""}`}
                onClick={() => setAppDropdownOpen((o) => !o)}
              >
                <span>
                  {selectedAppIds.length === 0
                    ? "All Protons"
                    : selectedAppIds.length === 1
                      ? (allApps.find((a) => a.id === selectedAppIds[0])
                          ?.name ?? "1 Proton")
                      : `${selectedAppIds.length} Protons`}
                </span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 9L12 15L18 9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {appDropdownOpen && (
                <div className={styles.filterDropdownMenu}>
                  <div className={styles.filterSearchRow}>
                    <input
                      className={styles.filterSearchInput}
                      placeholder="Search Protons…"
                      value={appSearchQuery}
                      onChange={(e) => setAppSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  <button
                    className={`${styles.filterDropdownItem} ${selectedAppIds.length === 0 ? styles.filterDropdownItemActive : ""}`}
                    onClick={() => {
                      handleAppToggle(null);
                      setAppDropdownOpen(false);
                      setAppSearchQuery("");
                    }}
                  >
                    <span className={styles.filterDropdownItemCheck}>
                      {selectedAppIds.length === 0 ? "✓" : ""}
                    </span>
                    <span>All Protons</span>
                  </button>
                  {allApps
                    .filter((a) =>
                      a.name
                        .toLowerCase()
                        .includes(appSearchQuery.toLowerCase()),
                    )
                    .map((a) => (
                      <button
                        key={a.id}
                        className={`${styles.filterDropdownItem} ${selectedAppIds.includes(a.id) ? styles.filterDropdownItemActive : ""}`}
                        onClick={() => handleAppToggle(a.id)}
                      >
                        <span className={styles.filterDropdownItemCheck}>
                          {selectedAppIds.includes(a.id) ? "✓" : ""}
                        </span>
                        <span>{a.name}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Federation filter — disabled while dashboard data is loading so stale
              options from the previous Proton selection are never interactable */}
          <div className={styles.prominentFilterGroup}>
            <span className={styles.prominentFilterLabel}>Federations</span>
            <div
              className={styles.filterDropdownWrapper}
              ref={fedFilterDropdownRef}
            >
              <button
                className={`${styles.prominentFilterBtn} ${isLoading || fedsByAppLoading ? styles.prominentFilterBtnShimmer : selectedFedIds.length > 0 ? styles.prominentFilterBtnActive : ""}`}
                onClick={() =>
                  !(isLoading || fedsByAppLoading) &&
                  setFedFilterDropdownOpen((o) => !o)
                }
                disabled={isLoading || fedsByAppLoading}
              >
                <span>
                  {selectedFedIds.length === 0
                    ? "All Federations"
                    : selectedFedIds.length === 1
                      ? (federationOptions.find(
                          (f) => f.id === selectedFedIds[0],
                        )?.name ?? "1 Federation")
                      : `${selectedFedIds.length} Federations`}
                </span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 9L12 15L18 9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {fedFilterDropdownOpen && (
                <div className={styles.filterDropdownMenu}>
                  <div className={styles.filterSearchRow}>
                    <input
                      className={styles.filterSearchInput}
                      placeholder="Search Federations…"
                      value={fedSearchQuery}
                      onChange={(e) => setFedSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  {selectedAppIds.length > 0 &&
                  !fedsByAppLoading &&
                  federationOptions.length === 0 ? (
                    <div className={styles.filterDropdownEmpty}>
                      No federations found for
                      <br />
                      the selected application.
                    </div>
                  ) : (
                    <>
                      <button
                        className={`${styles.filterDropdownItem} ${selectedFedIds.length === 0 ? styles.filterDropdownItemActive : ""}`}
                        onClick={() => {
                          handleFedFilterToggle(null);
                          setFedFilterDropdownOpen(false);
                          setFedSearchQuery("");
                        }}
                      >
                        <span className={styles.filterDropdownItemCheck}>
                          {selectedFedIds.length === 0 ? "✓" : ""}
                        </span>
                        <span>All Federations</span>
                      </button>
                      {federationOptions
                        .filter((f) =>
                          f.name
                            .toLowerCase()
                            .includes(fedSearchQuery.toLowerCase()),
                        )
                        .map((f) => (
                          <button
                            key={f.id}
                            className={`${styles.filterDropdownItem} ${selectedFedIds.includes(f.id) ? styles.filterDropdownItemActive : ""}`}
                            onClick={() => handleFedFilterToggle(f.id)}
                          >
                            <span className={styles.filterDropdownItemCheck}>
                              {selectedFedIds.includes(f.id) ? "✓" : ""}
                            </span>
                            <span>{f.name}</span>
                          </button>
                        ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <button className={styles.clearFiltersLink} onClick={clearAllFilters}>
            Clear filters
          </button>
          <button
            className={styles.dateRangeBtn}
            onClick={() => setDatePickerOpen(true)}
          >
            <span>{formatDateLabel()}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M19 4H5C3.9 4 3 4.9 3 6V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V10H19V20ZM19 8H5V6H19V8ZM9 14H7V12H9V14ZM13 14H11V12H13V14ZM17 14H15V12H17V14ZM9 18H7V16H9V18ZM13 18H11V16H13V18ZM17 18H15V16H17V18Z"
                fill="var(--Primary-Color-Shade-1)"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className={styles.lastRefreshedRow}>
        <span className={styles.lastRefreshed}>
          Last refreshed:{" "}
          <strong>
            {lastRefreshedAt ? (
              <RelativeTime value={lastRefreshedAt.toISOString()} />
            ) : isLoading ? (
              "Loading…"
            ) : (
              "—"
            )}
          </strong>
        </span>
      </div>

      {/* White scrollable container */}
      <div className={styles.scrollWrapper}>
        <div className={styles.cardsContainer}>
          <KPICardContainer cards={kpiCards} isLoading={dashboardLoading} />
        </div>

        <div className={styles.container}>
          <LineChartCard
            key={chartResetKey}
            title="Month wise event type distribution of count/sum"
            xAxisKey="month_label"
            filterLabel="All Events"
            data={lineChartData}
            yTickFormatter={formatCount}
            isLoading={dashboardLoading}
            hasError={isFulfilled && !!dataErrors.eventMonth}
            lines={[
              { key: "query", name: "Query", color: "#0D6AB3" },
              {
                key: "marked_engaged",
                name: "Marked/Engaged",
                color: "#2ACDFF",
              },
              {
                key: "audience_transferred",
                name: "Audience Transferred",
                color: "#F59E0B",
              },
            ]}
            onMonthClick={(label, metric, visibleKeys) => {
              // Single event type visible → server-side filter via eventType param
              const singleEventType =
                visibleKeys.length === 1 ? visibleKeys[0] : null;
              openDrill(
                "Month wise Event Type Distribution",
                singleEventType,
                label,
                "event",
                undefined,
                {
                  dayLevelChartMetric:
                    metric === "count" ? "query_count" : "total_count",
                  // Client-side filter only when multiple (but not all) event types are visible
                  allowedDimensionKeys:
                    !singleEventType && visibleKeys.length < 3
                      ? visibleKeys
                      : undefined,
                },
              );
            }}
          />

          <FederationSummaryCard
            key={`fed-${chartResetKey}`}
            title={"Federation wise\nSummary"}
            secondDropdownLabel="Federations"
            xAxisKey="month_label"
            data={federationChartData}
            yTickFormatter={formatCount}
            isLoading={dashboardLoading}
            hasError={isFulfilled && !!dataErrors.federationMonth}
            lines={federationLines}
            eventTypeOptions={fedEventTypes}
            selectedEventType={fedEventType}
            onEventTypeChange={setFedEventType}
            dateRange={dateRange}
            filterFederationIds={
              selectedFedIds.length > 0
                ? selectedFedIds
                : appFederationOptions.map((f) => f.id)
            }
            filterApplicationIds={selectedAppIds}
            filterSelectedFedIds={selectedFedIds}
            secondDropdownLoading={fedsByAppLoading}
            onMonthClick={(label, metric, selectedKeys) => {
              const allSelected = selectedKeys.length >= federationLines.length;
              const singleKey =
                !allSelected && selectedKeys.length === 1
                  ? selectedKeys[0]
                  : null;
              const entityId = singleKey ? singleKey.split("::")[0] : null;
              openDrill(
                "Federation Summary",
                fedEventType,
                label,
                "federation",
                // Single entity → server-side API filter
                entityId ? { federation_id: entityId } : undefined,
                {
                  dayLevelChartMetric:
                    metric === "query" ? "query_count" : "total_count",
                  // Client-side filter only for 2+ specific entities
                  allowedDimensionKeys:
                    !allSelected && !entityId
                      ? selectedKeys.map((k) => k.split("::")[0])
                      : undefined,
                },
              );
            }}
          />
        </div>

        <div className={styles.twoColContainer}>
          <FederationSummaryCard
            key={`org-${chartResetKey}`}
            title={"Organization wise\nSummary"}
            secondDropdownLabel="Organizations"
            xAxisKey="month_label"
            data={orgChartData}
            yTickFormatter={formatCount}
            isLoading={dashboardLoading}
            hasError={isFulfilled && !!dataErrors.organisationMonth}
            lines={orgLines}
            eventTypeOptions={orgEventTypes}
            selectedEventType={orgEventType}
            onEventTypeChange={setOrgEventType}
            dateRange={dateRange}
            filterFederationIds={selectedFedIds}
            filterApplicationIds={selectedAppIds}
            filterSelectedFedIds={selectedFedIds}
            filterOrganizations={appOrganizationOptions}
            allEntityLines={allOrgLines}
            onMonthClick={(label, metric, selectedKeys) => {
              const allSelected = selectedKeys.length >= allOrgLines.length;
              const singleKey =
                !allSelected && selectedKeys.length === 1
                  ? selectedKeys[0]
                  : null;
              const entityId = singleKey ? singleKey.split("::")[0] : null;
              openDrill(
                "Organization Summary",
                orgEventType,
                label,
                "organization",
                entityId ? { organization_id: entityId } : undefined,
                {
                  dayLevelChartMetric:
                    metric === "query" ? "query_count" : "total_count",
                  allowedDimensionKeys:
                    !allSelected && !entityId
                      ? selectedKeys.map((k) => k.split("::")[0])
                      : undefined,
                },
              );
            }}
          />

          <FederationSummaryCard
            key={`adserver-${chartResetKey}`}
            title={"Ad Server\nSummary"}
            secondDropdownLabel="Ad Servers"
            queryMetricLabel="Activation Count"
            xAxisKey="month_label"
            data={adserverChartData}
            yTickFormatter={formatCount}
            isLoading={dashboardLoading}
            hasError={isFulfilled && !!dataErrors.adserverMonth}
            lines={adserverLines}
            eventTypeOptions={adserverEventTypes}
            selectedEventType={adserverEventType}
            onEventTypeChange={setAdserverEventType}
            dateRange={dateRange}
            filterFederationIds={selectedFedIds}
            filterApplicationIds={selectedAppIds}
            filterSelectedFedIds={selectedFedIds}
            filterAdserverTypes={appAdserverTypes ?? undefined}
            allEntityLines={allAdserverLines}
            onMonthClick={(label, metric, selectedKeys) => {
              const allSelected = selectedKeys.length >= allAdserverLines.length;
              // Adserver keys are plain type strings (e.g. "aws"), no composite format
              const singleType =
                !allSelected && selectedKeys.length === 1
                  ? selectedKeys[0]
                  : null;
              openDrill(
                "Ad Server Summary",
                null,
                label,
                "adserver",
                singleType
                  ? { destination_adserver_type: singleType }
                  : undefined,
                {
                  dayLevelChartMetric:
                    metric === "query" ? "query_count" : "total_count",
                  allowedDimensionKeys:
                    !allSelected && !singleType ? selectedKeys : undefined,
                },
              );
            }}
          />
        </div>

        <div className={styles.tableContainer}>
          <DataTable
            title="Audience Transferred"
            subHeader={
              <div className={styles.tableTabsRow}>
                <button
                  className={`${styles.tableTab} ${tableTab === "activation" ? styles.tableTabActive : ""}`}
                  onClick={() => setTableTab("activation")}
                >
                  Activation
                </button>
                <button
                  className={`${styles.tableTab} ${tableTab === "enrichment" ? styles.tableTabActive : ""}`}
                  onClick={() => setTableTab("enrichment")}
                >
                  Enrichment
                </button>
              </div>
            }
            columns={tableColumns}
            rows={tableRows}
            isLoading={tableLoading}
            hasError={tableMonthError}
            monthFilter={{
              options: tableMonthOptions,
              selected: selectedTableMonth,
              onChange: setSelectedTableMonth,
            }}
          />
        </div>
      </div>

      <DatePickerModal
        datePickerOpen={datePickerOpen}
        onClose={() => setDatePickerOpen(false)}
        onApply={handleDateApply}
        initialStart={dateRange.start ? `${dateRange.start}T00:00` : ""}
        initialEnd={dateRange.end ? `${dateRange.end}T00:00` : ""}
      />

      <DrillDownDrawer
        target={drillTarget}
        onClose={() => setDrillTarget(null)}
      />
    </LayoutContainer>
  );
};

export default ManagementDashboard;
